import Phaser from "phaser";
import type { Unit } from "../combat/types";
import { animKey, hasUnitAnimation } from "./animations";
import { resolveSpriteClass } from "../art/UnitArt";
import type { UnitAnimState } from "./manifest";

// Plays a state animation on a unit sprite.
// - If a real spritesheet exists for this class+state, plays it via Phaser anims.
// - Otherwise applies a tween that approximates the state (idle bob, attack lunge, etc.)
//   so static procedural sprites still feel alive.

interface IdleTween {
  tween?: Phaser.Tweens.Tween;
}

const idleTweens = new WeakMap<Phaser.GameObjects.Sprite, IdleTween>();

const stopIdleFallback = (sprite: Phaser.GameObjects.Sprite): void => {
  const t = idleTweens.get(sprite);
  if (t?.tween) {
    t.tween.stop();
    t.tween = undefined;
  }
  // The walk fallback wobbles `angle`; reset it so the next state starts clean.
  sprite.angle = 0;
};

/** Whether this unit has real frames for `state` (not the procedural fallback). */
export const hasUnitState = (scene: Phaser.Scene, unit: Unit, state: UnitAnimState): boolean =>
  hasUnitAnimation(resolveSpriteClass(scene, unit), state);

export const playUnitState = (
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Sprite,
  unit: Unit,
  state: UnitAnimState
): void => {
  // Mirror UnitArt.ensureUnitTexture exactly (via the shared resolver) so
  // animation lookups stay consistent with whichever spritesheet the
  // static texture came from — real class art first, override stand-in
  // second.
  const spriteClass = resolveSpriteClass(scene, unit);
  if (hasUnitAnimation(spriteClass, state)) {
    stopIdleFallback(sprite);
    const key = animKey(spriteClass, state);
    sprite.play(key, true);
    // A flinch is a one-shot that hands back to idle when it ends — left
    // alone the unit froze on its recoil frame until its next action.
    // (Attack is handed back by the lunge; death holds its last frame.)
    if (state === "hit") {
      sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE_KEY + key, () => {
        if (sprite.active && sprite.anims.currentAnim?.key === key) {
          playUnitState(scene, sprite, unit, "idle");
        }
      });
    }
    return;
  }

  // Procedural fallback. Stop any previous fallback tween first.
  stopIdleFallback(sprite);

  switch (state) {
    case "idle":
      // Intentionally NO idle bob here for procedural sprites.
      //
      // BattleScene.startBreathing() owns the idle y-bob and is called
      // alongside every playUnitState(idle) on the battle map. It anchors the
      // bob to the unit view's authoritative `baseY`. This fallback used to
      // ALSO start an infinite y-tween, anchored to whatever `sprite.y`
      // happened to be at capture time — so two independent, never-cancelling
      // yoyo tweens ran on the same sprite with different periods. Their
      // combined offset drifted the sprite upward over repeated
      // move/idle cycles (the "floating in outer space" bug). Leaving idle
      // motion solely to startBreathing removes the conflict. Non-battle
      // callers of playUnitState(idle) simply get a still sprite, which is
      // correct — they don't want a competing bob either.
      break;
    case "walk": {
      // Tilt-wobble while moving. We CANNOT tween `y` here — animateMove owns
      // sprite.x/sprite.y and a competing y-tween causes the sprite to flicker
      // back-and-forth between the bob target and the per-step move target.
      sprite.angle = 0;
      const tween = scene.tweens.add({
        targets: sprite,
        angle: 4,
        duration: 130,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut"
      });
      idleTweens.set(sprite, { tween });
      break;
    }
    case "attack":
      // The lunge tween is owned by BattleScene.lunge() — nothing to do here.
      break;
    case "hit":
      // Flash handled at the call site.
      break;
    case "death":
      // Fade handled at the call site.
      break;
  }
};

// Reset to idle and clear any pending tweens. Useful after death cleanup or
// when a turn ends.
export const resetToIdle = (
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Sprite,
  unit: Unit
): void => {
  playUnitState(scene, sprite, unit, "idle");
};
